import { useEffect, useState, useRef } from 'react'
import { supabase } from '../lib/supabaseClient'
import { decryptField as decrypt, encryptField as encrypt } from '../lib/secureFields';
import { useCurrency } from '../hooks/useCurrency'
import { useToast } from '../components/useToast' 
import { useLanguage } from '../hooks/useLanguage'
import { usePageTour } from '../hooks/usePageTour'
import { getAhorroSteps } from '../tours/ahorroTour'

export default function Ahorros() {
  const { t } = useLanguage()
  const [user, setUser] = useState(null)
  const [savings, setSavings] = useState([])
  const [sharedSavings, setSharedSavings] = useState([])
  const [friends, setFriends] = useState([])
  const [balance, setBalance] = useState(0)
  const [totalSaved, setTotalSaved] = useState(0)
  const [loading, setLoading] = useState(true)
  const { format } = useCurrency()
  const [confirmDeleteSavingId, setConfirmDeleteSavingId] = useState(null)

  // CREAR
  const [showCreate, setShowCreate] = useState(false)
  const [newName, setNewName] = useState('')
  const [newDesc, setNewDesc] = useState('')
  const [newTarget, setNewTarget] = useState('')
  const [savingType, setSavingType] = useState('saving')
  const [shareWithFriend, setShareWithFriend] = useState(false)
  const [selectedFriendId, setSelectedFriendId] = useState('')
  const [creating, setCreating] = useState(false)

  // MODAL MOVIMIENTO
  const [selectedSaving, setSelectedSaving] = useState(null)
  const [selectedShared, setSelectedShared] = useState(null)
  const [amount, setAmount] = useState('')
  const [comprobanteFile, setComprobanteFile] = useState(null)
  const [comprobantePreview, setComprobantePreview] = useState(null)
  const [uploading, setUploading] = useState(false)
  const fileRef = useRef(null)

  //MODAL COMPLETADOS
  const [showCompleted, setShowCompleted] = useState(false)
  const [searchCompleted, setSearchCompleted] = useState('')

  //Alertbonito
   const toast = useToast()

   const iniciarTour = usePageTour(
  (t) => getAhorroSteps({
    t,
    hasPersonales: savings.length > 0,
    hasCompartidos: sharedSavings.length > 0,
    setUi: (ui) => {
      setShowCreate(ui === 'create')
      setSelectedSaving(ui === 'saving' ? (savings[0] ?? null) : null)
    }
  }),
  () => { setShowCreate(false); setSelectedSaving(null) }
)

  // VER COMPROBANTE
  const [viewingComprobante, setViewingComprobante] = useState(null)

  function handleAmountChange(value) {
    const limpio = value.replace(/\D/g, '')
    const formateado = new Intl.NumberFormat('es-CO').format(limpio)
    setAmount(formateado)
  }

  // ✅ PON ESTO EN SU LUGAR:
  const ALLOWED_MIME_TYPES = [
    'image/jpeg',
    'image/png',
    'image/webp',
    'image/heic',
    'image/heif'
  ]
  const MAX_SIZE_MB = 10
  const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024

  function handleFileChange(e) {
    const file = e.target.files[0]
    if (!file) return
    if (!ALLOWED_MIME_TYPES.includes(file.type)) {
      toast.warning(t('toast_imagen_invalida'))
      e.target.value = ''
      return
    }
    if (file.size > MAX_SIZE_BYTES) {
      toast.error(t('toast_imagen_max_mb').replace('{mb}', MAX_SIZE_MB))
      e.target.value = ''
      return
    }
    setComprobanteFile(file)
    setComprobantePreview(URL.createObjectURL(file))
  }

    async function loadData() {
      const { data: { session } } = await supabase.auth.getSession()
      const u = session?.user
      if (!u) return
      setUser(u)

      // AHORROS PERSONALES
      const { data: savingsData } = await supabase
        .from('savings').select('*').eq('user_id', u.id)

      const decryptedSavings = await Promise.all(
      (savingsData || []).map(async s => ({
      ...s,
      name: s.name ? await decrypt(s.name) : '',
      description: s.description
        ? await decrypt(s.description)
        : ''
      }))
      )

    const { data: allMovs } = await supabase
      .from('saving_transactions').select('*').eq('user_id', u.id)

    const withTotals = decryptedSavings.map(s => {
      const movs = (allMovs || []).filter(m => m.saving_id === s.id)
      const total = movs.reduce((acc, m) => m.type === 'deposit' ? acc + m.amount : acc - m.amount, 0)
      return { ...s, total }
    })
    setSavings(withTotals)
    setTotalSaved(withTotals.reduce((acc, s) => acc + s.total, 0))

    // AHORROS COMPARTIDOS
    const { data: sharedData } = await supabase
      .from('shared_savings').select('*')
      
      .or(`creator_id.eq.${u.id},partner_id.eq.${u.id}`)
      .neq('status', 'rejected')
      
      const decryptedShared = await Promise.all(
      (sharedData || []).map(async s => ({
        ...s,
        name: s.name ? await decrypt(s.name) : '',
        description: s.description
          ? await decrypt(s.description)
          : ''
      }))
    )

    if (sharedData && sharedData.length > 0) {
      // IDS de todas las personas involucradas
      const involvedIds = [...new Set(sharedData.flatMap(s => [s.creator_id, s.partner_id]))]
      const { data: profiles } = await supabase
        .from('profiles').select('id, full_name, avatar_path')
        .in('id', involvedIds)

      const sharedWithTotals = await Promise.all(decryptedShared.map(async s => {
        const { data: movs } = await supabase
          .from('shared_saving_transactions').select('*')
          .eq('shared_saving_id', s.id)

        const total = (movs || []).reduce((acc, m) =>
          m.type === 'deposit' ? acc + m.amount : acc - m.amount, 0)

        const myTotal = (movs || [])
          .filter(m => m.user_id === u.id && m.type === 'deposit')
          .reduce((acc, m) => acc + m.amount, 0)

        const partnerTotal = (movs || [])
          .filter(m => m.user_id !== u.id && m.type === 'deposit')
          .reduce((acc, m) => acc + m.amount, 0)

        const creatorProfile = profiles?.find(p => p.id === s.creator_id)
        const partnerProfile = profiles?.find(p => p.id === s.partner_id)

        return { ...s, total, myTotal, partnerTotal, movs: movs || [], creatorProfile, partnerProfile }
      }))

      setSharedSavings(sharedWithTotals)
    } else {
      setSharedSavings([])
    }

    // BALANCE
    const { data: transactions } = await supabase
      .from('transactions').select('*').eq('user_id', u.id)
    const totalIngresos = transactions?.filter(t => t.type === 'income').reduce((acc, t) => acc + t.amount, 0) || 0
    const totalGastos = transactions?.filter(t => t.type === 'expense').reduce((acc, t) => acc + t.amount, 0) || 0
    setBalance(totalIngresos - totalGastos)

    // AMIGOS
    const { data: friendsData } = await supabase
      .from('friends').select('*')
      .or(`user_id.eq.${u.id},friend_id.eq.${u.id}`)
      .eq('status', 'accepted')

    if (friendsData && friendsData.length > 0) {
      const friendIds = friendsData.map(f => f.user_id === u.id ? f.friend_id : f.user_id)
      const { data: friendProfiles } = await supabase
        .from('profiles').select('id, full_name, friend_id')
        .in('id', friendIds)
      const formatted = friendsData.map(f => {
        const realId = f.user_id === u.id ? f.friend_id : f.user_id
        return { ...f, profile: friendProfiles?.find(p => p.id === realId) }
      })
      setFriends(formatted)
    }

    setLoading(false)
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
useEffect(() => {
  loadData()

  const savingsChannel = supabase
    .channel('shared-savings-changes')
    .on('postgres_changes', {
      event: 'DELETE',
      schema: 'public',
      table: 'shared_savings'
    }, () => {
      setTimeout(() => loadData(), 500)
    })
    .subscribe()

  // escuchar notificaciones para recargar cuando llega confirmación
  supabase.auth.getSession().then(({ data: { session } }) => {
    if (!session?.user) return
    const notiChannel = supabase
      .channel('ahorros-noti-' + session.user.id)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'notifications',
        filter: `user_id=eq.${session.user.id}`
      }, (payload) => {
        if (payload.new.type === 'info' && payload.new.title?.includes('Eliminación aceptada')) {
          setTimeout(() => loadData(), 500)
        }
      })
      .subscribe()

    return () => supabase.removeChannel(notiChannel)
  })

  return () => supabase.removeChannel(savingsChannel)
// eslint-disable-next-line react-hooks/exhaustive-deps
}, [])


    // CREAR AHORRO
  async function createSaving() {
    if (!newName.trim()) { toast.error(t('toast_ahorro_ponle_nombre')); return }
    if (newName.trim().length > 50) { toast.error(t('toast_nombre_max_50')); return }
    if (newDesc.trim().length > 200) { toast.error(t('toast_desc_max_200')); return }
    if (shareWithFriend && !selectedFriendId) { toast.warning(t('toast_selecciona_amigo')); return }
    setCreating(true)

    const { data: { session } } = await supabase.auth.getSession()
    const u = session?.user
    if (!u) return

    if (shareWithFriend) {
      // AHORRO COMPARTIDO
      const friend = friends.find(f => {
        const realId = f.user_id === u.id ? f.friend_id : f.user_id
        return realId === selectedFriendId
      })
      const realPartnerId = friend?.user_id === u.id ? friend?.friend_id : friend?.user_id
      const friendName = friend?.profile?.full_name || t('ahorro_tu_amigo')

      const encryptedName = await encrypt(newName)

      const encryptedDesc = newDesc
        ? await encrypt(newDesc)
        : ''

      const { data: shared, error } = await supabase.from('shared_savings').insert({
        creator_id: u.id,
        partner_id: realPartnerId,
        name: encryptedName,        // ✅
        description: encryptedDesc, // ✅
        target_amount: parseFloat(newTarget.replace(/\./g, '')) || 0,
        type: savingType,
        status: 'pending'
      }).select().single()

      if (error) { toast.error(t('toast_error_crear')); setCreating(false); return }

      // NOTIFICACION AL PARTNER
      const myName = user?.user_metadata?.display_name || user?.user_metadata?.full_name || t('noti_alguien')
      const { error: notiError } = await supabase.from('notifications').insert({
        user_id: realPartnerId,
        type: 'shared_saving_request',
        title: `${myName} ${t('noti_invito_a_pre')} ${savingType === 'debt' ? t('noti_seguimiento_deuda') : t('noti_ahorro_compartido')}`,
        message: `"${newName}" — ${newDesc || t('noti_sin_descripcion')}`,
        metadata: {
          saving_id: shared.id,
          from_user_id: u.id,
          saving_name: newName,
          saving_type: savingType
        }
      })
      if (notiError) {
      toast.error(t('toast_demasiadas_solicitudes'))
      setCreating(false)
      return
    }

      toast.success(`${t('toast_solicitud_enviada_a')} ${friendName}`)
    } else {
      // AHORRO PERSONAL
    const encryptedName = await encrypt(newName)
    const encryptedDesc = newDesc
      ? await encrypt(newDesc)
      : ''

    await supabase.from('savings').insert({
      user_id: u.id,
      name: encryptedName,
      description: encryptedDesc,
      target_amount: parseFloat(newTarget.replace(/\./g, '')) || 0,
      type: savingType
    })
    }

    setShowCreate(false)
    setNewName('')
    setNewDesc('')
    setNewTarget('')
    setShareWithFriend(false)
    setSelectedFriendId('')
    setCreating(false)
    loadData()
  }

  // MOVER DINERO PERSONAL
  async function addMoney(type) {
    const { data: { session } } = await supabase.auth.getSession()
    const u = session?.user
    if (!u || !selectedSaving) return

    const value = parseFloat(amount.replace(/\./g, ''))
    if (!value || value <= 0) { toast.error(t('toast_cantidad_invalida')); return }
    if (type === 'deposit' && value > balance) { toast.error(t('toast_sin_dinero_disponible')); return }
    if (type === 'withdraw' && value > selectedSaving.total) { toast.error(t('toast_sin_suficiente_ahorro')); return }

    if (type === 'deposit' && selectedSaving.target_amount > 0) {
      const restante = selectedSaving.target_amount - selectedSaving.total
      if (value > restante) {
        toast.error(t('toast_solo_hasta_meta').replace('{monto}', format(restante)))
        return
      }
    }

    // SUBIR COMPROBANTE SI HAY
    let comprobanteUrl = null
    if (comprobanteFile) {
      setUploading(true)
      const ext = comprobanteFile.name.split('.').pop()
      const path = `${u.id}/${Date.now()}.${ext}`
      const { error: uploadError } = await supabase.storage
        .from('comprobantes').upload(path, comprobanteFile)
      if (!uploadError) {
        const { data: { publicUrl } } = supabase.storage.from('comprobantes').getPublicUrl(path)
        comprobanteUrl = publicUrl
      }
      setUploading(false)
    }

    await supabase.from('saving_transactions').insert({
      saving_id: selectedSaving.id,
      user_id: u.id,
      amount: value,
      type,
      ...(comprobanteUrl && { comprobante_url: comprobanteUrl })
    })

    await supabase.from('transactions').insert({
      user_id: u.id,
      type: type === 'deposit' ? 'expense' : 'income',
      amount: value,
      category: 'Ahorro',
      description: `${type === 'deposit' ? 'Ahorro' : 'Retiro'} ${selectedSaving.name}`,
      date: new Date().toLocaleDateString('en-CA')
    })

    setSelectedSaving(null)
    setAmount('')
    setComprobanteFile(null)
    setComprobantePreview(null)
    loadData()
  }

  // MOVER DINERO COMPARTIDO
  async function addMoneyShared(type) {
    const { data: { session } } = await supabase.auth.getSession()
    const u = session?.user
    if (!u || !selectedShared) return

    if (selectedShared.type === 'debt' && type === 'deposit' && selectedShared.creator_id !== u.id) {
      toast.warning(t('toast_solo_creador_pagos_deuda'))
      return
    }

    const value = parseFloat(amount.replace(/\./g, ''))
    if (!value || value <= 0) { toast.error(t('toast_cantidad_invalida')); return }
    if (type === 'deposit' && value > balance) { toast.error(t('toast_sin_dinero_disponible')); return }
    if (!comprobanteFile) { toast.warning(t('toast_debes_subir_comprobante')); return }

    setUploading(true)

    // 1. Subir comprobante primero
    const ext = comprobanteFile.name.split('.').pop()
    const storagePath = `${u.id}/${Date.now()}.${ext}`
    const { error: uploadError } = await supabase.storage
      .from('comprobantes').upload(storagePath, comprobanteFile)

    if (uploadError) { toast.error(t('toast_error_subir_comprobante')); setUploading(false); return }

    const { data: { publicUrl } } = supabase.storage.from('comprobantes').getPublicUrl(storagePath)

    // 2. Intentar insertar (RPC para depósitos, directo para retiros)
    if (type === 'deposit') {
      const { data: result, error: rpcError } = await supabase.rpc('deposit_shared_saving', {
        p_saving_id: selectedShared.id,
        p_user_id: u.id,
        p_amount: value,
        p_comprobante_url: publicUrl
      })

          // ✅ DESPUÉS — maneja todos los errores:
    if (rpcError || result?.error) {
      await supabase.storage.from('comprobantes').remove([storagePath])

      const mensajes = {
        'excede_meta': t('toast_excede_meta').replace('{monto}', `$${Number(result?.restante ?? 0).toLocaleString('es-CO')}`),
        'no_autorizado': t('toast_no_autorizado'),
        'solo_creador': t('toast_solo_creador_deuda'),
        'no_activo': t('toast_no_activo'),
        'no_existe': t('toast_no_existe')
      }

      toast.error(mensajes[result?.error] || t('toast_error_registrar_pago'))
      setUploading(false)
      return
    }
    } else {
      await supabase.from('shared_saving_transactions').insert({
        shared_saving_id: selectedShared.id,
        user_id: u.id,
        amount: value,
        type,
        comprobante_url: publicUrl
      })
    }

    // 3. Solo si todo salió bien: registrar en balance y notificar
    await supabase.from('transactions').insert({
      user_id: u.id,
      type: type === 'deposit' ? 'expense' : 'income',
      amount: value,
      category: 'Ahorro compartido',
      description: `${type === 'deposit' ? 'Aporte' : 'Retiro'} "${selectedShared.name}"`,
      date: new Date().toLocaleDateString('en-CA')
    })

    const partnerId = selectedShared.creator_id === u.id
      ? selectedShared.partner_id
      : selectedShared.creator_id

    const myName = user?.user_metadata?.display_name || user?.user_metadata?.full_name || t('noti_tu_companero')

    const { error: notiError } = await supabase.from('notifications').insert({
      user_id: partnerId,
      type: 'info',
      title: `💰 ${t('noti_nuevo_aporte')} ${type === 'deposit' ? t('noti_aporte') : t('noti_retiro')} ${t('noti_en')} "${selectedShared.name}"`,
      message: `${myName} ${type === 'deposit' ? t('noti_aporto') : t('noti_retiro_verbo')} ${format(value)}. ${t('noti_toca_ver_comprobante')}`,
      metadata: { saving_id: selectedShared.id, comprobante_url: publicUrl }
    })
    if (notiError) {
    toast.error(t('toast_demasiadas_solicitudes_warn'))
    setCreating(false)
    return
  }

    setUploading(false)
    setSelectedShared(null)
    setAmount('')
    setComprobanteFile(null)
    setComprobantePreview(null)
    loadData()
  }

function handleDeleteSavingClick(id) {
    setConfirmDeleteSavingId(id)
  }

  async function deleteSaving() {
    const id = confirmDeleteSavingId
    if (!id) return
    setConfirmDeleteSavingId(null)

    const { data: { session } } = await supabase.auth.getSession()
    const u = session?.user
    if (!u) return

    const { data: movs } = await supabase.from('saving_transactions').select('*').eq('saving_id', id)
    const total = (movs || []).reduce((acc, m) => m.type === 'deposit' ? acc + m.amount : acc - m.amount, 0)

    if (total > 0) {
      await supabase.from('transactions').insert({
        user_id: u.id, type: 'income', amount: total,
        category: 'Ahorro', description: 'Eliminación de ahorro',
        date: new Date().toLocaleDateString('en-CA')
      })
    }
    await supabase.from('saving_transactions').delete().eq('saving_id', id)
    await supabase.from('savings').delete().eq('id', id)
    setSelectedSaving(null)
    toast.success(t('toast_ahorro_eliminado'))
    loadData()
  }
    async function requestDeleteShared(s) {
      const { data: { session } } = await supabase.auth.getSession()
      const u = session?.user
      if (!u) return

      const isCreator = s.creator_id === u.id
      const partnerId = isCreator ? s.partner_id : s.creator_id
      const myName = user?.user_metadata?.display_name || user?.user_metadata?.full_name || t('noti_tu_companero')

      const { error: notiError } = await supabase.from('notifications').insert({
        user_id: partnerId,
        type: 'shared_saving_delete_request',
        title: `${t('noti_eliminar_titulo_pre')} ${myName} ${t('noti_quiere_eliminar')} "${s.name}"`,
        message: `${t('noti_solicita_eliminar_pre')} ${s.type === 'debt' ? t('noti_seguimiento_deuda') : t('noti_ahorro_compartido')} ${t('noti_solicita_eliminar_post')}`,
        metadata: {
          saving_id: s.id,
          from_user_id: u.id,
          saving_name: s.name
        }
      })

      if (notiError) {
        toast.error(t('toast_demasiadas_solicitudes_warn'))
        return
      }

      toast.success(t('toast_solicitud_eliminacion_enviada'))
      loadData()
    }

    async function sendBuzz(s) {
    const { data: { session } } = await supabase.auth.getSession()
    const u = session?.user
    if (!u) return

    const partnerId = s.creator_id === u.id ? s.partner_id : s.creator_id
    const myName = user?.user_metadata?.display_name || user?.user_metadata?.full_name || t('noti_alguien')

    const { error } = await supabase.from('notifications').insert({
      user_id: partnerId,
      type: 'buzz',
      title: `${myName} ${t('noti_zumbido_titulo')}`,
      message: `${t('noti_revisa_tu')} "${s.name}" 👀`,
      metadata: {
        saving_id: s.id,
        saving_name: s.name,
        from_user_id: u.id,
      }
    })

    if (error) {
      toast.error(t('toast_zumbido_error'))
    } else {
      toast.success(t('toast_zumbido_enviado'))
    }
  }

  function getAvatarUrl(path) {
    if (!path) return ''
    const { data: { publicUrl } } = supabase.storage.from('avatars').getPublicUrl(path)
    return publicUrl
  }

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--color-bg)', display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column', gap: 20 }}>
        <div style={{ width: 70, height: 70, borderRadius: 20, background: 'linear-gradient(135deg, #f59e0b, #b45309)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 32, animation: 'pulse 1.5s infinite' }}>🐷</div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--color-text)', marginBottom: 6 }}>{t('ahorros_loading_title')}</div>
          <div style={{ fontSize: 14, color: 'var(--color-muted)' }}>{t('ahorros_loading_desc')}</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {[0,1,2].map(i => <div key={i} style={{ width: 10, height: 10, borderRadius: '50%', background: '#f59e0b', animation: `bounce 0.6s infinite ${i * 0.15}s` }} />)}
        </div>
        <style>{`
          @keyframes pulse { 0%,100%{transform:scale(1);opacity:1} 50%{transform:scale(1.08);opacity:0.85} }
          @keyframes bounce { 0%,80%,100%{transform:translateY(0);opacity:0.5} 40%{transform:translateY(-8px);opacity:1} }
        `}</style>
      </div>
    )
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--color-bg)' }}>

    <header style={{ background: 'var(--color-surface)', borderBottom: '1px solid var(--color-border)', padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, zIndex: 10, backdropFilter: 'blur(10px)' }}>
      <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
        {t('ahorros_header')}
      </h2>

      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <button
          onClick={iniciarTour}
          title={t('tour_ayuda')}
          style={{ width: 30, height: 30, borderRadius: '50%', border: '1px solid var(--color-border)', background: 'transparent', color: 'var(--color-text)', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}
        >
          ?
        </button>

        <button onClick={() => window.history.back()} style={{ padding: '8px 14px', borderRadius: 12, border: '1px solid var(--color-border)', background: 'transparent', fontSize: 13, cursor: 'pointer', color: 'var(--color-text)', fontWeight: 600, transition: '0.2s' }}
          onMouseEnter={e => e.currentTarget.style.border = '1px solid #3b82f6'}
          onMouseLeave={e => e.currentTarget.style.border = '1px solid var(--color-border)'}
        >{t('volver')}</button>
      </div>
    </header>

      <main style={{ maxWidth: 640, margin: '0 auto', padding: 20, paddingBottom: 40 }}>

        {/* RESUMEN */}
        <div data-tour="ahorro-resumen" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 20 }}>
          <div style={card}>
            <div style={labelStyle}>{t('ahorros_disponible')}</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: '#22c55e', marginTop: 4 }}>{format(balance)}</div>
          </div>
          <div style={card}>
            <div style={labelStyle}>{t('ahorros_total_ahorrado')}</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: '#f59e0b', marginTop: 4 }}>{format(totalSaved)}</div>
          </div>
        </div>

        {/* BOTON CREAR */}
      <button data-tour="ahorro-nuevo" style={btnGreenFull} onClick={() => setShowCreate(true)}>{t('ahorros_nuevo_btn')}</button>

        {/* ── AHORROS PERSONALES ── */}
      {savings.length > 0 && (
        <div data-tour="ahorro-personales" style={{ marginBottom: 28 }}>
          <div style={sectionTitle}>{t('ahorros_personales')}</div>
            {savings.map(s => {
              const progreso = s.target_amount ? Math.min((s.total / s.target_amount) * 100, 100) : 0
              return (
                <div key={s.id} onClick={() => setSelectedSaving(s)} style={{ ...savingCard, border: s.type === 'debt' ? '1px solid rgba(239,68,68,0.3)' : '1px solid rgba(245,158,11,0.3)', background: s.type === 'debt' ? 'rgba(127,29,29,0.12)' : 'rgba(245,158,11,0.06)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 16, color: 'var(--color-text)' }}>{s.name}</div>
                      <div style={{ fontSize: 11, marginTop: 2, color: s.type === 'debt' ? '#ef4444' : '#f59e0b', fontWeight: 600 }}>{s.type === 'debt' ? t('ahorro_tipo_deuda') : t('ahorro_tipo_ahorro')}</div>
                      {s.description && <div style={{ fontSize: 12, color: '#aaa', marginTop: 2 }}>{s.description}</div>}
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 20, fontWeight: 800, color: s.type === 'debt' ? '#ef4444' : '#f59e0b' }}>{format(s.total)}</div>
                      {s.target_amount > 0 && <div style={{ fontSize: 11, color: '#aaa' }}>/ {format(s.target_amount)}</div>}
                    </div>
                  </div>
                  {s.target_amount > 0 && (
                    <div style={{ marginTop: 10 }}>
                      <div style={progressBarStyle}>
                        <div style={{ width: `${progreso}%`, background: s.type === 'debt' ? '#ef4444' : '#22c55e', height: '100%', borderRadius: 999, transition: '0.4s ease' }} />
                      </div>
                      <div style={{ fontSize: 11, color: '#aaa', marginTop: 4, textAlign: 'right' }}>{progreso.toFixed(0)}%</div>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}

        {/* ── AHORROS COMPARTIDOS ── */}
        {sharedSavings.length > 0 && (() => {
          const activos = sharedSavings.filter(s =>
            s.status !== 'completed' &&
            !(s.target_amount > 0 && s.total >= s.target_amount)
          )
          const completados = sharedSavings.filter(s =>
            s.target_amount > 0 && s.total >= s.target_amount
          )
          const completadosFiltrados = completados.filter(s =>
            s.name.toLowerCase().includes(searchCompleted.toLowerCase())
          )

        return (
          <div data-tour="ahorro-compartidos" style={{ marginBottom: 28 }}>
            <div style={sectionTitle}>{t('ahorros_compartidos')}</div>

              {/* ACTIVOS */}
              {activos.length === 0 && completados.length === 0 && (
                <div style={{ textAlign: 'center', padding: '20px', color: 'var(--color-muted)', fontSize: 13 }}>
                  {t('ahorros_sin_compartidos')}
                </div>
              )}

              {activos.map(s => {
                const progreso = s.target_amount ? Math.min((s.total / s.target_amount) * 100, 100) : 0
                const isCreator = s.creator_id === user?.id
                const partnerProfile = isCreator ? s.partnerProfile : s.creatorProfile
                const isPending = s.status === 'pending'
                const isReadOnly = s.type === 'debt' && !isCreator

                return (
                  <div key={s.id}
                    onClick={() => !isPending && setSelectedShared(s)}
                    style={{
                      ...savingCard,
                      border: isPending ? '1px solid rgba(99,102,241,0.4)' : s.type === 'debt' ? '1px solid rgba(239,68,68,0.3)' : '1px solid rgba(34,197,94,0.3)',
                      background: isPending ? 'rgba(99,102,241,0.06)' : s.type === 'debt' ? 'rgba(127,29,29,0.12)' : 'rgba(34,197,94,0.06)',
                      cursor: isPending ? 'default' : 'pointer',
                      opacity: isPending ? 0.8 : 1
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <div style={{ fontWeight: 700, fontSize: 16, color: 'var(--color-text)' }}>{s.name}</div>
                          <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: isPending ? 'rgba(99,102,241,0.15)' : 'rgba(34,197,94,0.15)', color: isPending ? '#818cf8' : '#22c55e' }}>
                            {isPending ? t('ahorro_pendiente') : t('ahorro_activo')}
                          </span>
                          <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: 'rgba(255,255,255,0.06)', color: '#aaa' }}>{t('ahorro_compartido_badge')}</span>
                        </div>
                        <div style={{ fontSize: 11, marginTop: 2, color: s.type === 'debt' ? '#ef4444' : '#f59e0b', fontWeight: 600 }}>{s.type === 'debt' ? t('ahorro_tipo_seguimiento_deuda') : t('ahorro_tipo_ahorro_compartido')}</div>
                        {s.description && <div style={{ fontSize: 12, color: '#aaa', marginTop: 2 }}>{s.description}</div>}
                      </div>
                      <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: 10 }}>
                        <div style={{ fontSize: 20, fontWeight: 800, color: s.type === 'debt' ? '#ef4444' : '#22c55e' }}>{format(s.total)}</div>
                        {s.target_amount > 0 && <div style={{ fontSize: 11, color: '#aaa' }}>/ {format(s.target_amount)}</div>}
                      </div>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 6 }}>
                      <button
                        onClick={e => { e.stopPropagation(); sendBuzz(s) }}
                        style={{
                          padding: '5px 12px',
                          borderRadius: 999,
                          border: '1px solid rgba(99,102,241,0.35)',
                          background: 'rgba(99,102,241,0.08)',
                          color: '#a5b4fc',
                          cursor: 'pointer',
                          fontSize: 13,
                          fontWeight: 700,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 5,
                          transition: '0.2s',
                          letterSpacing: 0.2,
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = 'rgba(99,102,241,0.18)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'rgba(99,102,241,0.08)'}
                        title={`${t('ahorro_zumbido_title')} ${partnerProfile.full_name}`}
                      >
                        {t('ahorro_zumbido_btn')}
                      </button>
                    </div>
                    {partnerProfile && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderRadius: 10, background: 'rgba(255,255,255,0.04)', marginBottom: 10 }}>
                        <div style={{ width: 28, height: 28, borderRadius: '50%', overflow: 'hidden', background: 'rgba(59,130,246,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: '#60a5fa', flexShrink: 0 }}>
                          {partnerProfile.avatar_path
                            ? <img src={getAvatarUrl(partnerProfile.avatar_path)} alt="av" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            : (partnerProfile.full_name || 'U').charAt(0).toUpperCase()
                          }
                        </div>
                        <div style={{ fontSize: 12, color: '#aaa' }}>
                          {isCreator ? t('ahorro_con') : t('ahorro_de')} <span style={{ color: 'var(--color-text)', fontWeight: 600 }}>{partnerProfile.full_name}</span>
                        </div>
                        {isReadOnly && <span style={{ marginLeft: 'auto', fontSize: 10, color: '#6b7280', fontWeight: 600 }}>{t('ahorro_solo_lectura')}</span>}
                      </div>
                    )}

                    {!isPending && (
                      <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                        <div style={{ flex: 1, padding: '6px 10px', borderRadius: 8, background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.15)' }}>
                          <div style={{ fontSize: 10, color: '#6b7280' }}>{s.type === 'debt' ? t('ahorro_total_pagado') : t('ahorro_tu_aporte')}</div>
                          <div style={{ fontSize: 13, fontWeight: 700, color: '#22c55e' }}>{format(s.myTotal)}</div>
                        </div>
                        {s.type === 'saving' && (
                          <div style={{ flex: 1, padding: '6px 10px', borderRadius: 8, background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.15)' }}>
                            <div style={{ fontSize: 10, color: '#6b7280' }}>{t('ahorro_su_aporte')}</div>
                            <div style={{ fontSize: 13, fontWeight: 700, color: '#60a5fa' }}>{format(s.partnerTotal)}</div>
                          </div>
                        )}
                      </div>
                    )}

                    {s.target_amount > 0 && !isPending && (
                      <div>
                        <div style={progressBarStyle}>
                          <div style={{ width: `${progreso}%`, background: s.type === 'debt' ? '#ef4444' : '#22c55e', height: '100%', borderRadius: 999, transition: '0.4s ease' }} />
                        </div>
                        <div style={{ fontSize: 11, color: '#aaa', marginTop: 4, textAlign: 'right' }}>{progreso.toFixed(0)}%</div>
                      </div>
                    )}

                    {isPending && (
                      <div style={{ fontSize: 12, color: '#818cf8', textAlign: 'center', padding: '4px 0' }}>
                        {t('ahorro_esperando_acepte_pre')} {partnerProfile?.full_name || t('ahorro_tu_amigo')} {t('ahorro_esperando_acepte_post')}
                      </div>
                    )}
                  </div>
                )
              })}

              {/* SECCIÓN COMPLETADOS */}
              {completados.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  <button
                    onClick={() => setShowCompleted(!showCompleted)}
                    style={{ width: '100%', padding: '12px 16px', borderRadius: 14, border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.03)', color: '#aaa', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 13, fontWeight: 600, transition: '0.2s' }}
                  >
                    <span>🎉 {completados.length === 1 ? t('ahorros_completados_1') : t('ahorros_completados_n').replace('{n}', completados.length)}</span>
                    <span style={{ transition: '0.2s', transform: showCompleted ? 'rotate(180deg)' : 'rotate(0deg)', display: 'inline-block' }}>▼</span>
                  </button>

                  {showCompleted && (
                    <div style={{ marginTop: 10 }}>
                      {/* BUSCADOR */}
                      <input
                        type="text"
                        placeholder={t('ahorros_buscar_placeholder')}
                        value={searchCompleted}
                        onChange={e => setSearchCompleted(e.target.value)}
                        style={{ ...inputStyle, marginBottom: 10, background: 'rgba(255,255,255,0.04)', fontSize: 13, padding: '10px 14px' }}
                      />

                      {completadosFiltrados.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '16px', color: 'var(--color-muted)', fontSize: 13 }}>
                          {t('ahorros_sin_resultados')}
                        </div>
                      ) : completadosFiltrados.map(s => {
                        const isCreator = s.creator_id === user?.id
                        const partnerProfile = isCreator ? s.partnerProfile : s.creatorProfile

                        return (
                          <div key={s.id} style={{ ...savingCard, border: '1px solid rgba(34,197,94,0.2)', background: 'rgba(34,197,94,0.04)', opacity: 0.85 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                  <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--color-text)' }}>{s.name}</div>
                                  <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: 'rgba(34,197,94,0.15)', color: '#22c55e' }}>{t('ahorro_completado_badge')}</span>
                                </div>
                                {partnerProfile && (
                                  <div style={{ fontSize: 12, color: '#aaa', marginTop: 4 }}>
                                    {isCreator ? t('ahorro_con') : t('ahorro_de')} <span style={{ color: 'var(--color-text)', fontWeight: 600 }}>{partnerProfile.full_name}</span>
                                  </div>
                                )}
                                <div style={{ fontSize: 13, fontWeight: 700, color: '#22c55e', marginTop: 4 }}>
                                  {format(s.total)} / {format(s.target_amount)}
                                </div>
                              </div>

                              {/* BOTON ELIMINAR */}
                              <button
                                onClick={e => { e.stopPropagation(); requestDeleteShared(s) }}
                                style={{ marginLeft: 12, padding: '8px 12px', borderRadius: 10, border: 'none', background: 'rgba(239,68,68,0.12)', color: '#ef4444', cursor: 'pointer', fontSize: 13, fontWeight: 700, flexShrink: 0, transition: '0.2s' }}
                                onMouseEnter={e => e.currentTarget.style.background = 'rgba(239,68,68,0.25)'}
                                onMouseLeave={e => e.currentTarget.style.background = 'rgba(239,68,68,0.12)'}
                              >
                                {t('ahorro_eliminar_btn')}
                              </button>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          )
        })()}

          {savings.length === 0 && sharedSavings.length === 0 && (
            <div data-tour="ahorro-vacio" style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--color-muted)' }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>🐷</div>
            <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 6 }}>{t('ahorros_sin_ahorros_titulo')}</div>
            <div style={{ fontSize: 13 }}>{t('ahorros_sin_ahorros_desc')}</div>
          </div>
        )}

      </main>

      {/* ── MODAL CREAR ── */}
      {showCreate && (
        <div style={overlay}>
          <div style={{ ...modal, maxHeight: '90vh', overflowY: 'auto' }}>
            <h3 style={{ margin: 0, fontSize: 18, color: 'white' }}>
              {savingType === 'saving' ? t('ahorro_modal_nuevo_ahorro') : t('ahorro_modal_nueva_deuda')}
            </h3>

            {/* TIPO */}
            <div data-tour="ahorro-tipo" style={{ display: 'flex', gap: 10 }}>
              {['saving', 'debt'].map(tp => (
                <button key={tp} onClick={() => setSavingType(tp)} style={{ flex: 1, padding: 12, borderRadius: 12, border: savingType === tp ? `2px solid ${tp === 'saving' ? '#f59e0b' : '#ef4444'}` : '1px solid var(--color-border)', background: savingType === tp ? `rgba(${tp === 'saving' ? '245,158,11' : '239,68,68'},0.12)` : '#111', color: savingType === tp ? (tp === 'saving' ? '#f59e0b' : '#ef4444') : '#aaa', cursor: 'pointer', fontWeight: 600, transition: '0.2s' }}>
                  {tp === 'saving' ? t('ahorro_tipo_ahorro') : t('ahorro_tipo_deuda')}
                </button>
              ))}
            </div>

            <div data-tour="ahorro-datos" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <input type="text" placeholder={t('ahorro_nombre_placeholder')} value={newName} onChange={e => setNewName(e.target.value)} style={inputStyle} />
              <input type="text" placeholder={t('ahorro_descripcion_placeholder')} value={newDesc} onChange={e => setNewDesc(e.target.value)} style={inputStyle} />
              <input type="text" placeholder={t('ahorro_meta_placeholder')} value={newTarget} onChange={e => { const l = e.target.value.replace(/\D/g, ''); setNewTarget(new Intl.NumberFormat('es-CO').format(l)) }} style={inputStyle} />
            </div>

            {/* COMPARTIR CON AMIGO */}
            <div data-tour="ahorro-compartir" style={{ padding: '12px 14px', borderRadius: 12, border: '1px solid var(--color-border)', background: 'rgba(255,255,255,0.03)' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: 'white' }}>{t('ahorro_compartir_amigo')}</div>
                  <div style={{ fontSize: 11, color: '#aaa', marginTop: 2 }}>
                    {savingType === 'debt' ? t('ahorro_compartir_desc_deuda') : t('ahorro_compartir_desc_ahorro')}
                  </div>
                </div>
                <button
                  onClick={() => setShareWithFriend(!shareWithFriend)}
                  style={{ width: 44, height: 24, borderRadius: 999, border: 'none', cursor: 'pointer', background: shareWithFriend ? '#22c55e' : '#333', transition: '0.2s', position: 'relative', flexShrink: 0 }}
                >
                  <div style={{ width: 18, height: 18, borderRadius: '50%', background: 'white', position: 'absolute', top: 3, left: shareWithFriend ? 23 : 3, transition: '0.2s' }} />
                </button>
              </div>

              {shareWithFriend && (
                <div style={{ marginTop: 12 }}>
                  {friends.length === 0 ? (
                    <div style={{ fontSize: 12, color: '#f59e0b', textAlign: 'center', padding: 8 }}>{t('ahorro_sin_amigos')}</div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {friends.map(f => {
                        const realId = f.user_id === user?.id ? f.friend_id : f.user_id
                        const isSelected = selectedFriendId === realId
                        return (
                          <button key={f.id} onClick={() => setSelectedFriendId(realId)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 10, border: isSelected ? '2px solid #22c55e' : '1px solid var(--color-border)', background: isSelected ? 'rgba(34,197,94,0.1)' : 'rgba(255,255,255,0.03)', cursor: 'pointer', transition: '0.2s' }}>
                            <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'rgba(59,130,246,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700, color: '#60a5fa', flexShrink: 0 }}>
                              {(f.profile?.full_name || 'U').charAt(0).toUpperCase()}
                            </div>
                            <div style={{ textAlign: 'left' }}>
                              <div style={{ fontSize: 13, fontWeight: 600, color: 'white' }}>{f.profile?.full_name || t('ahorro_usuario_generico')}</div>
                              <div style={{ fontSize: 11, color: '#aaa' }}>{t('ahorro_id_label')} {f.profile?.friend_id}</div>
                            </div>
                            {isSelected && <div style={{ marginLeft: 'auto', color: '#22c55e', fontSize: 16 }}>✓</div>}
                          </button>
                        )
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>

            <button style={{ ...btnGreen, opacity: creating ? 0.6 : 1 }} onClick={createSaving} disabled={creating}>
              {creating ? t('ahorro_creando') : t('ahorro_guardar_btn')}
            </button>
            <button style={btnGray} onClick={() => { setShowCreate(false); setShareWithFriend(false); setSelectedFriendId('') }}>{t('cancelar')}</button>
          </div>
        </div>
      )}

      {/* ── MODAL MOVIMIENTO PERSONAL ── */}
      {selectedSaving && (
        <div style={overlay}>
          <div style={{ ...modal, maxHeight: '90vh', overflowY: 'auto' }}>
            <h3 style={{ margin: 0, color: 'white' }}>{selectedSaving.name}</h3>
            <div style={{ fontSize: 12, color: selectedSaving.type === 'debt' ? '#ef4444' : '#f59e0b' }}>
              {selectedSaving.type === 'debt' ? t('ahorro_tipo_deuda') : t('ahorro_tipo_ahorro')} · {format(selectedSaving.total)} {t('ahorro_acumulado')}
            </div>

            <input type="text" placeholder={t('ahorro_monto_placeholder')} value={amount} onChange={e => handleAmountChange(e.target.value)} style={inputStyle}
              onFocus={e => { e.target.style.border = '1px solid #22c55e'; e.target.style.boxShadow = '0 0 0 3px rgba(34,197,94,0.15)' }}
              onBlur={e => { e.target.style.border = '1px solid var(--color-border)'; e.target.style.boxShadow = 'none' }}
            />

            {/* COMPROBANTE OPCIONAL */}
            <div data-tour="ahorro-mov-comprobante" style={{ padding: '10px 12px', borderRadius: 12, border: '1px dashed rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.02)' }}>
              <div style={{ fontSize: 12, color: '#aaa', marginBottom: 8 }}>{t('ahorro_comprobante_opcional')}</div>
              {comprobantePreview
                ? <div style={{ position: 'relative' }}>
                    <img src={comprobantePreview} alt="comprobante" style={{ width: '100%', borderRadius: 8, maxHeight: 160, objectFit: 'cover' }} />
                    <button onClick={() => { setComprobanteFile(null); setComprobantePreview(null) }} style={{ position: 'absolute', top: 6, right: 6, width: 24, height: 24, borderRadius: '50%', background: 'rgba(0,0,0,0.6)', border: 'none', color: 'white', cursor: 'pointer', fontSize: 12 }}>✕</button>
                  </div>
                : <button onClick={() => fileRef.current?.click()} style={{ width: '100%', padding: '10px', borderRadius: 8, border: 'none', background: 'rgba(255,255,255,0.05)', color: '#aaa', cursor: 'pointer', fontSize: 13 }}>{t('ahorro_subir_imagen')}</button>
              }
              <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif" onChange={handleFileChange} style={{ display: 'none' }} />
            </div>

            <div data-tour="ahorro-mov-acciones" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <button style={btnGreen} onClick={() => addMoney('deposit')} disabled={uploading}>{uploading ? t('ahorro_subiendo') : t('ahorro_meter_dinero')}</button>
              <button style={btnRed} onClick={() => addMoney('withdraw')} disabled={uploading}>{t('ahorro_sacar_dinero')}</button>
              <button style={{ ...btnRed, background: '#7f1d1d' }} onClick={() => handleDeleteSavingClick(selectedSaving.id)}>{t('ahorro_eliminar_btn')}</button>
            </div>

            <button style={btnGray} onClick={() => { setSelectedSaving(null); setAmount(''); setComprobanteFile(null); setComprobantePreview(null) }}>{t('cerrar_modal')}</button>
          </div>
        </div>
      )}
      {/* ── MODAL CONFIRMAR ELIMINAR AHORRO ── */}
      {confirmDeleteSavingId && (
        <div style={overlay}>
          <div style={{ ...modal, maxWidth: 320, textAlign: 'center' }}>
            <div style={{ fontSize: 36 }}>🗑️</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'white' }}>
              {t('ahorro_confirmar_eliminar_titulo')}
            </div>
            <div style={{ fontSize: 13, color: '#6b7280' }}>
              {t('ahorro_confirmar_eliminar_desc')}
            </div>
            <button style={btnRed} onClick={deleteSaving}>{t('si_eliminar')}</button>
            <button style={btnGray} onClick={() => setConfirmDeleteSavingId(null)}>{t('cancelar')}</button>
          </div>
        </div>
      )}

      {/* ── MODAL MOVIMIENTO COMPARTIDO ── */}
      {selectedShared && (
        <div style={overlay}>
          <div style={{ ...modal, maxHeight: '90vh', overflowY: 'auto' }}>
            <h3 style={{ margin: 0, color: 'white' }}>{selectedShared.name}</h3>
            <div style={{ fontSize: 12, color: selectedShared.type === 'debt' ? '#ef4444' : '#22c55e' }}>
              {selectedShared.type === 'debt' ? t('ahorro_tipo_seguimiento_deuda') : t('ahorro_tipo_ahorro_compartido')} · {format(selectedShared.total)} total
            </div>

            {/* HISTORIAL DE MOVIMIENTOS CON COMPROBANTES */}
            {selectedShared.movs && selectedShared.movs.length > 0 && (
              <div style={{ maxHeight: 180, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div style={{ fontSize: 11, color: '#aaa', fontWeight: 600 }}>{t('ahorro_historial')}</div>
                {[...selectedShared.movs].reverse().map((m, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderRadius: 10, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)' }}>
                    <div style={{ fontSize: 14 }}>{m.type === 'deposit' ? '➕' : '➖'}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: m.type === 'deposit' ? '#22c55e' : '#ef4444' }}>
                        {m.type === 'deposit' ? '+' : '-'}{format(m.amount)}
                      </div>
                      <div style={{ fontSize: 10, color: '#6b7280' }}>
                        {new Date(m.created_at).toLocaleDateString('es-CO', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </div>
                    {m.comprobante_url && (
                      <button onClick={() => setViewingComprobante(m.comprobante_url)} style={{ fontSize: 18, background: 'none', border: 'none', cursor: 'pointer' }} title={t('ahorro_ver_comprobante_title')}>🧾</button>
                    )}
                  </div>
                ))}
              </div>
            )}
            

            {/* SOLO MOSTRAR INPUT SI NO ES READONLY */}
            {selectedShared.target_amount > 0 && selectedShared.total >= selectedShared.target_amount ? (
              <div style={{ padding: '18px 14px', borderRadius: 14, background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.25)', textAlign: 'center' }}>
                <div style={{ fontSize: 36, marginBottom: 8 }}>🎉</div>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#22c55e', marginBottom: 4 }}>
                  {selectedShared.type === 'debt' ? t('ahorro_deuda_saldada') : t('ahorro_meta_alcanzada')}
                </div>
                <div style={{ fontSize: 13, color: '#6b7280' }}>
                  {selectedShared.type === 'debt'
                    ? t('ahorro_pagaste_todo')
                    : t('ahorro_meta_lograda_juntos')}
                </div>
              </div>
            ) : !(selectedShared.type === 'debt' && selectedShared.creator_id !== user?.id) ? (
              <>
                <input type="text" placeholder={t('ahorro_monto_placeholder')} value={amount} onChange={e => handleAmountChange(e.target.value)} style={inputStyle}
                  onFocus={e => { e.target.style.border = '1px solid #22c55e'; e.target.style.boxShadow = '0 0 0 3px rgba(34,197,94,0.15)' }}
                  onBlur={e => { e.target.style.border = '1px solid var(--color-border)'; e.target.style.boxShadow = 'none' }}
                />
                <div style={{ padding: '10px 12px', borderRadius: 12, border: comprobantePreview ? '1px solid rgba(34,197,94,0.3)' : '1px dashed rgba(239,68,68,0.4)', background: 'rgba(255,255,255,0.02)' }}>
                  <div style={{ fontSize: 12, marginBottom: 8, display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#aaa' }}>{t('ahorro_comprobante_pago')}</span>
                    <span style={{ color: '#ef4444', fontWeight: 700 }}>{t('ahorro_obligatorio')}</span>
                  </div>
                  {comprobantePreview
                    ? <div style={{ position: 'relative' }}>
                        <img src={comprobantePreview} alt="comprobante" style={{ width: '100%', borderRadius: 8, maxHeight: 160, objectFit: 'cover' }} />
                        <button onClick={() => { setComprobanteFile(null); setComprobantePreview(null) }} style={{ position: 'absolute', top: 6, right: 6, width: 24, height: 24, borderRadius: '50%', background: 'rgba(0,0,0,0.6)', border: 'none', color: 'white', cursor: 'pointer', fontSize: 12 }}>✕</button>
                      </div>
                    : <button onClick={() => fileRef.current?.click()} style={{ width: '100%', padding: '12px', borderRadius: 8, border: 'none', background: 'rgba(239,68,68,0.08)', color: '#f87171', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>{t('ahorro_subir_comprobante')}</button>
                  }
                  <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif" onChange={handleFileChange} style={{ display: 'none' }} />
                </div>
                <button style={{ ...btnGreen, opacity: uploading ? 0.6 : 1 }} onClick={() => addMoneyShared('deposit')} disabled={uploading}>{uploading ? t('ahorro_subiendo') : t('ahorro_registrar_pago')}</button>
                {selectedShared.type === 'saving' && (
                  <button style={{ ...btnRed, opacity: uploading ? 0.6 : 1 }} onClick={() => addMoneyShared('withdraw')} disabled={uploading}>{t('ahorro_sacar_dinero')}</button>
                )}
                <button onClick={() => requestDeleteShared(selectedShared)} style={{ ...btnRed, background: '#7f1d1d', opacity: uploading ? 0.6 : 1 }} disabled={uploading} > {t('ahorro_solicitar_eliminacion')}
              </button>
              </>
            ) : (
              <div style={{ padding: '14px', borderRadius: 12, background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.2)', textAlign: 'center' }}>
                <div style={{ fontSize: 24, marginBottom: 6 }}>👁</div>
                <div style={{ fontSize: 13, color: '#818cf8', fontWeight: 600 }}>{t('ahorro_modo_observador')}</div>
                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>{t('ahorro_modo_observador_desc')}</div>
              </div>
            )}

            <button style={btnGray} onClick={() => { setSelectedShared(null); setAmount(''); setComprobanteFile(null); setComprobantePreview(null) }}>{t('cerrar_modal')}</button>
          </div>
        </div>
      )}

      {/* ── VISOR DE COMPROBANTE ── */}
      {viewingComprobante && (
        <div style={{ ...overlay, zIndex: 9999 }} onClick={() => setViewingComprobante(null)}>
          <div style={{ maxWidth: '90vw', maxHeight: '90vh', borderRadius: 16, overflow: 'hidden', position: 'relative' }} onClick={e => e.stopPropagation()}>
            <img src={viewingComprobante} alt="comprobante" style={{ width: '100%', maxHeight: '85vh', objectFit: 'contain', display: 'block' }} />
            <button onClick={() => setViewingComprobante(null)} style={{ position: 'absolute', top: 10, right: 10, width: 32, height: 32, borderRadius: '50%', background: 'rgba(0,0,0,0.7)', border: 'none', color: 'white', cursor: 'pointer', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
          </div>
        </div>
      )}

    </div>
  )
}

// ESTILOS
const card = { flex: 1, padding: 14, borderRadius: 14, background: 'var(--color-surface)', border: '1px solid var(--color-border)' }
const labelStyle = { fontSize: 12, color: '#aaa', fontWeight: 600 }
const sectionTitle = { fontSize: 13, fontWeight: 700, color: 'var(--color-muted)', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 12 }
const savingCard = { background: 'var(--color-surface)', borderRadius: 16, padding: 16, marginBottom: 12, cursor: 'pointer', transition: '0.2s' }
const progressBarStyle = { height: 6, background: 'rgba(255,255,255,0.06)', borderRadius: 999, overflow: 'hidden' }
const btnGreenFull = { width: '100%', marginBottom: 16, padding: '12px', borderRadius: 12, background: '#22c55e', color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 14 }
const btnGreen = { padding: 12, borderRadius: 10, background: '#22c55e', color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 700 }
const btnRed = { padding: 12, borderRadius: 10, background: '#c52222', color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 700 }
const btnGray = { padding: 12, borderRadius: 10, background: '#333', color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 600 }
const overlay = { position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.6)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 100, padding: 16, boxSizing: 'border-box' }
const modal = { background: '#111', padding: 20, borderRadius: 20, width: '100%', maxWidth: 360, display: 'flex', flexDirection: 'column', gap: 10, color: '#fff' }
const inputStyle = { padding: '12px 14px', borderRadius: 12, border: '1px solid var(--color-border)', background: '#1a1a1a', color: '#fff', fontSize: 14, outline: 'none', width: '100%', boxSizing: 'border-box', transition: '0.2s' }